import * as React from 'react'
import { useEffect, useCallback, useMemo, memo } from 'react'
import moment from 'moment'
import { addDays, subDays } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useTypedParams } from 'react-router-typesafe-routes/dom'
import useVehicleActivityService from '../../../services/ActivityService'
import { useDate } from '@/modules/drivers/stores/dateStore'
import WeeklyNavigation from '../components/navigations/WeeklyNavigation'
import { useActivity } from '@/modules/vehicles/stores/activityStore'
import Timeline from '@/modules/drivers/tabs/activities/components_new/Timeline'
import { TimelineProps } from '@/modules/drivers/tabs/activities/components_new/timelineProps'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  faEmptySetSolid,
  faClose,
  FaIcon,
  faMagnifyingGlassSolid,
} from '@/lib/fa'
import { LoaderIcon } from '@/app/features/icons/Loader'
import {
  calculateSummary,
  getFormattedCalculation,
} from '@/app/components/timeline/timelineHelper'
import { ActivityType } from 'app/constants/activity'
import { VEHICLE_ROUTES } from '../../../routes-typed'

const PL_WEEKDAY_ABBREV = ['Nd', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'Sb']

const formatDayHeader = (date: string): string => {
  const m = moment(date)
  return `${PL_WEEKDAY_ABBREV[m.day()].toUpperCase()}, ${m.format('DD-MM-YYYY')}`
}

const VehicleDayChart = memo(
  ({
    day,
    chartWidth,
    data,
    t,
    onDayClick,
    rangeMode,
    rangeStart,
    rangeEnd,
    pin1Time: globalPin1,
    pin2Time: globalPin2,
    onRangeClick,
  }: {
    day: { date: string; activities: any[]; count: number; isVisible: boolean }
    chartWidth: number
    data: any
    t: any
    onDayClick: (date: string) => void
    rangeMode?: boolean
    rangeStart?: moment.Moment | null
    rangeEnd?: moment.Moment | null
    pin1Time?: moment.Moment | null
    pin2Time?: moment.Moment | null
    onRangeClick?: (time: moment.Moment) => void
  }) => {
    const { setType } = useActivity()
    const dayStartTime = useMemo(
      () => moment(`${day.date} 00:00:00`),
      [day.date],
    )

    const [selectedActivity, setSelectedActivity] = React.useState(null)
    const hasAnyPin = globalPin1 != null || globalPin2 != null

    const dayChartWrapperRef = React.useRef<HTMLDivElement>(null)
    const [localHoverPos, setLocalHoverPos] = React.useState<number | null>(
      null,
    )
    const [localHoverTime, setLocalHoverTime] =
      React.useState<moment.Moment | null>(null)

    const CHART_OFFSET = 0
    const timelineStartX = CHART_OFFSET
    const usableTimelineWidth = chartWidth - CHART_OFFSET

    const dayStartMoment = useMemo(
      () => moment(day.date).startOf('day'),
      [day.date],
    )
    const dayEndMoment = useMemo(
      () => dayStartMoment.clone().add(1, 'day'),
      [dayStartMoment],
    )
    const totalSecondsInDay = useMemo(
      () => dayEndMoment.diff(dayStartMoment, 'seconds'),
      [dayStartMoment, dayEndMoment],
    )

    const timeToPosition = useCallback(
      (time: moment.Moment) => {
        const seconds = time.diff(dayStartMoment, 'seconds')
        const clamped = Math.max(0, Math.min(totalSecondsInDay, seconds))
        return (
          timelineStartX + (clamped / totalSecondsInDay) * usableTimelineWidth
        )
      },
      [dayStartMoment, totalSecondsInDay, timelineStartX, usableTimelineWidth],
    )

    const positionToTime = useCallback(
      (posX: number) => {
        const relX = posX - timelineStartX
        const fraction = Math.max(0, Math.min(1, relX / usableTimelineWidth))
        return dayStartMoment
          .clone()
          .add(fraction * totalSecondsInDay, 'seconds')
      },
      [dayStartMoment, totalSecondsInDay, timelineStartX, usableTimelineWidth],
    )

    const snapToEdge = useCallback(
      (time: moment.Moment) => {
        const allActivities = data?.activities || []
        const SNAP_THRESHOLD = 300
        let bestTime = time
        let bestDist = Infinity
        for (const activity of allActivities as any[]) {
          for (const edgeStr of [activity.startTime, activity.endTime]) {
            if (!edgeStr) continue
            const edge = moment(edgeStr)
            const dist = Math.abs(time.diff(edge, 'seconds'))
            if (dist < bestDist && dist <= SNAP_THRESHOLD) {
              bestDist = dist
              bestTime = edge.clone()
            }
          }
        }
        return bestTime
      },
      [data?.activities],
    )

    const handleRangeChartClick = useCallback(
      (e: React.MouseEvent) => {
        if (!dayChartWrapperRef.current || !onRangeClick) return
        const rect = dayChartWrapperRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const rawTime = positionToTime(x)
        const snappedTime = snapToEdge(rawTime)
        onRangeClick(snappedTime)
      },
      [positionToTime, snapToEdge, onRangeClick],
    )

    const handleRangeMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (!dayChartWrapperRef.current) return
        const rect = dayChartWrapperRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const rawTime = positionToTime(x)
        const snappedTime = snapToEdge(rawTime)
        setLocalHoverPos(timeToPosition(snappedTime))
        setLocalHoverTime(snappedTime)
      },
      [positionToTime, snapToEdge, timeToPosition],
    )

    const handleRangeMouseLeave = useCallback(() => {
      setLocalHoverPos(null)
      setLocalHoverTime(null)
    }, [])

    const handleFirstPinClick = useCallback(
      (e: React.MouseEvent) => {
        if (hasAnyPin || !dayChartWrapperRef.current || !onRangeClick) return
        const rect = dayChartWrapperRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        if (x < CHART_OFFSET) return
        const rawTime = positionToTime(x)
        const snappedTime = snapToEdge(rawTime)
        onRangeClick(snappedTime)
      },
      [hasAnyPin, positionToTime, snapToEdge, onRangeClick],
    )

    const dayOverlay = useMemo(() => {
      if (!rangeStart || !rangeEnd) return null
      const hasOverlap =
        rangeStart.isBefore(dayEndMoment) && rangeEnd.isAfter(dayStartMoment)
      if (!hasOverlap) return { type: 'outside' as const }
      const overlapStart = moment.max(rangeStart, dayStartMoment)
      const overlapEnd = moment.min(rangeEnd, dayEndMoment)
      const startPos = timeToPosition(overlapStart)
      const endPos = timeToPosition(overlapEnd)
      return {
        type: 'partial' as const,
        leftOverlayWidth: startPos,
        selectionLeft: startPos,
        selectionWidth: endPos - startPos,
        rightOverlayLeft: endPos,
        rightOverlayWidth: chartWidth - endPos,
      }
    }, [
      rangeStart,
      rangeEnd,
      dayStartMoment,
      dayEndMoment,
      timeToPosition,
      chartWidth,
    ])

    const pin1PosOnDay = useMemo(() => {
      if (
        !globalPin1 ||
        !globalPin1.isSameOrAfter(dayStartMoment) ||
        !globalPin1.isBefore(dayEndMoment)
      )
        return null
      return timeToPosition(globalPin1)
    }, [globalPin1, dayStartMoment, dayEndMoment, timeToPosition])

    const pin2PosOnDay = useMemo(() => {
      if (
        !globalPin2 ||
        !globalPin2.isSameOrAfter(dayStartMoment) ||
        !globalPin2.isBefore(dayEndMoment)
      )
        return null
      return timeToPosition(globalPin2)
    }, [globalPin2, dayStartMoment, dayEndMoment, timeToPosition])

    const activities = data?.activities || []
    const formatedDate = moment(dayStartTime)

    const filteredActivities = useMemo(() => {
      return activities.filter((activity) => {
        const dayStart = formatedDate.clone()
        const dayEnd = formatedDate.clone().add(1, 'day')
        const activityEnd = activity.endTime
          ? moment(activity.endTime)
          : moment(formatedDate).endOf('day')

        return (
          moment(activity.startTime).isBefore(dayEnd) &&
          activityEnd.isAfter(dayStart)
        )
      })
    }, [activities, formatedDate])

    const filteredPlaces = useMemo(() => {
      const dayEnd = dayStartMoment.clone().add(1, 'day')
      return (data?.places || []).filter(
        (place) =>
          moment(place.startTime).isBefore(dayEnd) &&
          moment(place.endTime).isAfter(dayStartMoment),
      )
    }, [data?.places, dayStartMoment])

    React.useEffect(() => {
      setType('weekly')
    }, [])

    return (
      <div
        className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded tw-bg-white tw-shadow-sm"
        style={{
          opacity: dayOverlay?.type === 'outside' ? 0.3 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5">
          <div
            className="tw-grid tw-cursor-pointer hover:tw-opacity-70"
            onClick={() => onDayClick(day.date)}
          >
            <FaIcon icon={faMagnifyingGlassSolid} className="tw-h-3 tw-w-3" />
          </div>
          <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base">
            {formatDayHeader(day.date)}
          </span>
        </div>
        <div
          ref={dayChartWrapperRef}
          className="tw-relative"
          onClick={!hasAnyPin ? handleFirstPinClick : undefined}
        >
          <Timeline
            key={`vehicle-weekly-${day.date}`}
            {...({
              startTime: dayStartTime,
              days: 1,
              width: chartWidth,
              height: 91,
              extraCurrentDate: day.date,
              translate: t,
              activities: filteredActivities,
              places: filteredPlaces,
              cardInserts: [],
              dayActivities: [],
              violations: [],
              dailyRests: [],
              dailyPartialRegularRests: [],
              weeklyRests: [],
              weeklyReducedRests: [],
              breaks: [],
              partialBreaks: [],
              weekRecompenses: [],
              outOfScopes: [],
              changeAreas: [],
              existingDelegations: [],
              minimalPaymentCountries: [],
              tmsData: [],
              countries: [],
              delegationMarkers: [],
              allEvents: [],
              allTripEvents: [],
              allPreviousTripEvents: [],
              periodSelections: [],
              selectedActivity: selectedActivity,
              editedActivity: null,
              isAddingNew: false,
              canEdit: false,
              canGenerateAttestation: false,
              flattenChart: true,
          
              simpleMode: false,
              disableTooltips: hasAnyPin,
              showViolations: false,
              showRests: false,
              showRestParts: false,
              showDayActivities: false,
              showTrips: false,
              showDelegations: false,
              editMode: false,
              onSelected: setSelectedActivity,
              onContextMenuChanged: () => {},
              onAdd: () => {},
              onEdit: () => {},
              onViolationSelected: () => {},
              onPeriodSelectionsChanged: () => {},
            } as unknown as TimelineProps)}
          />
        {rangeMode && (
          <>
            {hasAnyPin && (
              <div
                style={{
                  position: 'absolute',
                  left: CHART_OFFSET,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 500,
                  cursor: 'crosshair',
                }}
                onClick={handleRangeChartClick}
                onMouseMove={handleRangeMouseMove}
                onMouseLeave={handleRangeMouseLeave}
              />
            )}
            {dayOverlay?.type === 'partial' && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: dayOverlay.leftOverlayWidth,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.75)',
                    zIndex: 400,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: dayOverlay.rightOverlayLeft,
                    top: 0,
                    width: dayOverlay.rightOverlayWidth,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.75)',
                    zIndex: 400,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: dayOverlay.selectionLeft,
                    top: 0,
                    width: dayOverlay.selectionWidth,
                    bottom: 0,
                    background: 'rgba(74, 96, 255, 0.08)',
                    border: '1px dashed rgba(74, 96, 255, 0.4)',
                    zIndex: 401,
                    pointerEvents: 'none',
                  }}
                />
              </>
            )}
            {pin1PosOnDay != null && (
              <div
                style={{
                  position: 'absolute',
                  left: pin1PosOnDay,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'var(--et-color-semantic-primary-foreground)',
                  zIndex: 450,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    bottom: -16,
                    left: -16,
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: 'var(--et-color-semantic-primary-foreground)',
                    padding: '1px 4px',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {globalPin1?.format('HH:mm')}
                </div>
              </div>
            )}
            {pin2PosOnDay != null && (
              <div
                style={{
                  position: 'absolute',
                  left: pin2PosOnDay,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'var(--et-color-semantic-primary-foreground)',
                  zIndex: 450,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    bottom: -16,
                    left: -16,
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: 'var(--et-color-semantic-primary-foreground)',
                    padding: '1px 4px',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {globalPin2?.format('HH:mm')}
                </div>
              </div>
            )}
            {localHoverPos != null && (
              <div
                style={{
                  position: 'absolute',
                  left: localHoverPos,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: 'rgba(74, 96, 255, 0.5)',
                  zIndex: 460,
                  pointerEvents: 'none',
                }}
              >
                {localHoverTime && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: -16,
                      left: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--et-color-semantic-primary-foreground)',
                      background: 'rgba(255,255,255,0.9)',
                      padding: '0 3px',
                      borderRadius: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {localHoverTime.format('HH:mm')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.day.date === nextProps.day.date &&
      prevProps.day.count === nextProps.day.count &&
      prevProps.chartWidth === nextProps.chartWidth &&
      prevProps.data === nextProps.data &&
      prevProps.rangeMode === nextProps.rangeMode &&
      prevProps.rangeStart?.valueOf() === nextProps.rangeStart?.valueOf() &&
      prevProps.rangeEnd?.valueOf() === nextProps.rangeEnd?.valueOf() &&
      prevProps.pin1Time?.valueOf() === nextProps.pin1Time?.valueOf() &&
      prevProps.pin2Time?.valueOf() === nextProps.pin2Time?.valueOf()
    )
  },
)

const Weekly = () => {
  const {
    companyId,
    vehicleId,
    date: currentDate,
  } = useTypedParams(VEHICLE_ROUTES.weeklyActivity)
  const navigate = useNavigate()
  const { setSingle, formatApiDate } = useDate()
  const { getVehicleActivity } = useVehicleActivityService()
  const { t } = useLocalize()
  const { setType, slot } = useActivity()
  const [data, setData] = React.useState<any>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [allDays, setAllDays] = React.useState<
    Array<{
      date: string
      activities: any[]
      count: number
      isVisible: boolean
    }>
  >([])

  const [pin1Time, setPin1Time] = React.useState<moment.Moment | null>(null)
  const [pin2Time, setPin2Time] = React.useState<moment.Moment | null>(null)

  const hasAnyPin = pin1Time != null || pin2Time != null

  const handleRangeClick = useCallback(
    (time: moment.Moment) => {
      if (!pin1Time) {
        setPin1Time(time)
      } else if (!pin2Time) {
        setPin2Time(time)
      } else {
        setPin1Time(time)
        setPin2Time(null)
      }
    },
    [pin1Time, pin2Time],
  )

  const clearRange = useCallback(() => {
    setPin1Time(null)
    setPin2Time(null)
  }, [])

  const sortedRange = useMemo(() => {
    if (!pin1Time || !pin2Time) return null
    if (pin1Time.isBefore(pin2Time)) return { start: pin1Time, end: pin2Time }
    return { start: pin2Time, end: pin1Time }
  }, [pin1Time, pin2Time])

  const rangeSummary = useMemo(() => {
    if (!sortedRange || !data?.activities) return null

    const allActivities = (data.activities as any[]).map((a) => ({
      ...a,
      endTime: a.endTime || moment(a.startTime).endOf('day').toISOString(),
    }))

    const summary = calculateSummary(
      sortedRange.start,
      sortedRange.end,
      allActivities,
    )
    const formatted = getFormattedCalculation(summary)
    const totalMs = sortedRange.end.diff(sortedRange.start)
    const totalDuration = moment.duration(totalMs)
    const totalHours = Math.floor(totalDuration.asHours())
    const totalMinutes = totalDuration.minutes()

    return {
      ...formatted,
      totalSelected: `${totalHours}h ${totalMinutes.toString().padStart(2, '0')}min`,
      period: `${sortedRange.start.format('DD.MM HH:mm')} - ${sortedRange.end.format('DD.MM HH:mm')}`,
    }
  }, [sortedRange, data?.activities])

  const ITEMS_PER_BLOCK = 8
  const [scrollOffset, setScrollOffset] = React.useState(0)
  const isScrollingRef = React.useRef(false)
  const centerDateRef = React.useRef(currentDate)

  const getWeeklyActivity = async () => {
    try {
      setIsLoading(true)
      const dateObj = new Date(centerDateRef.current)
      const result = await getVehicleActivity({
        companyId,
        vehicleId,
        startTime: formatApiDate(subDays(dateObj, 25)),
        endTime: formatApiDate(addDays(dateObj, 25)),
        slot: slot,
      })
      if (result?.activities) {
        result.activities = result.activities.map((a) => ({
          ...a,
          isVehicleSource: true,
        }))
      }
      setData(result || null)

      const days: Array<{
        date: string
        activities: any[]
        count: number
        isVisible: boolean
      }> = []

      for (let i = -25; i <= 25; i++) {
        const dayDate = addDays(dateObj, i)
        const dayStr = moment(dayDate).format('YYYY-MM-DD')
        const dayActivities =
          result?.activities?.filter((activity) => {
            const activityDate = moment(activity.startTime).format('YYYY-MM-DD')
            return activityDate === dayStr
          }) || []

        days.push({
          date: dayStr,
          activities: dayActivities,
          count: dayActivities.length,
          isVisible: false,
        })
      }

      setAllDays(days)
      setScrollOffset(25)
      setIsLoading(false)
    } catch (error) {
      console.error('Weekly: Error fetching vehicle weekly activity:', error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isScrollingRef.current) {
      centerDateRef.current = currentDate
      getWeeklyActivity()
    } else {
      isScrollingRef.current = false
    }
  }, [currentDate, vehicleId, companyId, slot])

  React.useLayoutEffect(() => {
    setSingle(currentDate)
  }, [currentDate])

  React.useEffect(() => {
    setType('weekly')
  }, [])

  const visibleDays = useMemo(() => {
    const startIdx = scrollOffset
    const endIdx = startIdx + ITEMS_PER_BLOCK
    return allDays.map((day, idx) => ({
      ...day,
      isVisible: idx >= startIdx && idx < endIdx,
    }))
  }, [allDays, scrollOffset, ITEMS_PER_BLOCK])

  const maxScroll = Math.max(0, allDays.length - ITEMS_PER_BLOCK)

  const goToPreviousBlock = useCallback(() => {
    if (isLoading) return

    if (scrollOffset <= 2 && allDays.length > 0) {
      const firstDay = allDays[0]
      const newCenterDate = moment(firstDay.date)
        .subtract(7, 'days')
        .format('YYYY-MM-DD')
      isScrollingRef.current = false
      navigate(
        VEHICLE_ROUTES.weeklyActivity.buildPath({
          companyId,
          vehicleId,
          date: newCenterDate,
        }),
        { replace: true },
      )
      return
    }

    setScrollOffset((prev) => {
      const newOffset = Math.max(0, prev - 1)
      if (allDays.length > 0) {
        const firstVisibleDay = allDays[newOffset]
        if (firstVisibleDay) {
          isScrollingRef.current = true
          navigate(
            VEHICLE_ROUTES.weeklyActivity.buildPath({
              companyId,
              vehicleId,
              date: firstVisibleDay.date,
            }),
            { replace: true },
          )
        }
      }
      return newOffset
    })
  }, [scrollOffset, allDays, history, companyId, vehicleId, isLoading])

  const goToNextBlock = useCallback(() => {
    if (isLoading) return

    if (scrollOffset >= maxScroll - 2 && allDays.length > 0) {
      const lastDay = allDays[allDays.length - 1]
      const newCenterDate = moment(lastDay.date)
        .add(7, 'days')
        .format('YYYY-MM-DD')
      isScrollingRef.current = false
      navigate(
        VEHICLE_ROUTES.weeklyActivity.buildPath({
          companyId,
          vehicleId,
          date: newCenterDate,
        }),
        { replace: true },
      )
      return
    }

    setScrollOffset((prev) => {
      const newOffset = Math.min(maxScroll, prev + 1)
      if (allDays.length > 0) {
        const firstVisibleDay = allDays[newOffset]
        if (firstVisibleDay) {
          isScrollingRef.current = true
          navigate(
            VEHICLE_ROUTES.weeklyActivity.buildPath({
              companyId,
              vehicleId,
              date: firstVisibleDay.date,
            }),
            { replace: true },
          )
        }
      }
      return newOffset
    })
  }, [
    scrollOffset,
    maxScroll,
    allDays,
    history,
    companyId,
    vehicleId,
    isLoading,
  ])

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = React.useState(0)
  const prevButtonRef = React.useRef<HTMLButtonElement>(null)
  const nextButtonRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!wrapperRef.current) return

    const updateWidth = () => {
      if (wrapperRef.current) {
        setChartWidth(wrapperRef.current.offsetWidth)
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(wrapperRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const daysToRender = useMemo(() => {
    const startIdx = scrollOffset
    const endIdx = startIdx + ITEMS_PER_BLOCK
    return visibleDays.slice(startIdx, endIdx)
  }, [visibleDays, scrollOffset, ITEMS_PER_BLOCK])

  const handleDayClick = useCallback(
    (date: string) => {
      navigate(
        VEHICLE_ROUTES.dailyActivity.buildPath({
          companyId,
          vehicleId,
          date,
        }),
      )
    },
    [history, companyId, vehicleId],
  )

  const currentDateRange = useMemo(() => {
    if (daysToRender.length === 0) return ''
    const firstMoment = moment(daysToRender[0].date)
    const lastMoment = moment(daysToRender[daysToRender.length - 1].date)

    const firstDay = firstMoment.format('D')
    const lastDay = lastMoment.format('D')
    const firstMonth = t(
      `monthName.${firstMoment.format('MMMM')?.toLowerCase()}`,
    )
    const lastMonth = t(`monthName.${lastMoment.format('MMMM')?.toLowerCase()}`)
    const firstYear = firstMoment.format('YYYY')
    const lastYear = lastMoment.format('YYYY')

    const sameMonth = firstMoment.format('MM') === lastMoment.format('MM')
    const sameYear = firstYear === lastYear

    if (sameMonth && sameYear) {
      return `${firstDay} - ${lastDay} ${firstMonth} ${firstYear}`
    } else if (sameYear) {
      return `${firstDay} ${firstMonth} - ${lastDay} ${lastMonth} ${firstYear}`
    } else {
      return `${firstDay} ${firstMonth} ${firstYear} - ${lastDay} ${lastMonth} ${lastYear}`
    }
  }, [daysToRender, t])

  return (
    <div>
      <WeeklyNavigation
        goToPreviousBlock={goToPreviousBlock}
        goToNextBlock={goToNextBlock}
        isLoading={isLoading}
        wrapperRef={wrapperRef}
        prevButtonRef={prevButtonRef}
        nextButtonRef={nextButtonRef}
        currentDateRange={currentDateRange}
      />

      <div
        ref={wrapperRef}
        className="tw-flex tw-flex-col tw-overflow-hidden tw-bg-white"
        style={{
          position: 'relative',
          maxHeight: 'calc(100vh - 235px)',
          minHeight: 'calc(100vh - 235px)',
        }}
      >
        {sortedRange && rangeSummary && (
          <div
            style={{
              position: 'absolute',
              top: 4,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              pointerEvents: 'auto',
            }}
          >
            <button
              onClick={clearRange}
              className="tw-bg-primary"
              style={{
                border: 'none',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <FaIcon icon={faClose} />
            </button>
            <div
              style={{
                background: '#fff',
                border: '2px solid #fff',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 12,
                color: '#333',
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  borderRight: '1px solid #ddd',
                  paddingRight: 10,
                  lineHeight: '16px',
                }}
              >
                <div
                  style={{
                    color: 'var(--et-color-semantic-primary-foreground)',
                  }}
                >
                  {rangeSummary.period}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  &Sigma; {rangeSummary.totalSelected}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/driving-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.DRIVING]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/work-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.WORK]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/availability-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.AVAILABILITY]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/rest-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.REST]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/unknown.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.UNKNOWN]}
                </span>
              </div>
            </div>
          </div>
        )}
        {isLoading && (
          <div className="tw-flex tw-flex-1 tw-flex-col tw-place-items-center tw-items-center tw-justify-center tw-gap-2">
            <LoaderIcon className="tw-animate-spin" />
            <p className="tw-text-sm tw-text-gray-500">
              {t('driverModule.loading-activities')}
            </p>
          </div>
        )}

        {!isLoading && allDays.length === 0 && (
          <div className="tw-flex tw-flex-1 tw-flex-col tw-place-items-center tw-items-center tw-justify-center tw-gap-2">
            <FaIcon icon={faEmptySetSolid} />
            <div className="tw-text-sm tw-text-gray-500">
              {t('common.no-data')}
            </div>
          </div>
        )}

        {!isLoading && visibleDays.length > 0 && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            {daysToRender.map((day) => (
              <VehicleDayChart
                key={day.date}
                day={day}
                chartWidth={chartWidth || 1200}
                data={data}
                t={t}
                onDayClick={handleDayClick}
                rangeMode={true}
                rangeStart={sortedRange?.start || null}
                rangeEnd={sortedRange?.end || null}
                pin1Time={pin1Time}
                pin2Time={pin2Time}
                onRangeClick={handleRangeClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Weekly
